/**
 * useEntityInsights - Fetches trust stats + sighting patterns for an entity.
 * Both requests are fire-and-forget; the component renders incrementally as
 * each resolves. No loading spinners required — widgets return null until data.
 * @atomic @idempotent
 */
import { useState, useEffect } from 'react'
import { entityAPI } from '../services/api/entities'
import { createLogger } from '../utils/logger'

const logger = createLogger('useEntityInsights')

/**
 * @param {string|null} entityId
 * @returns {{ trustStats, sightingPatternCounts, peakDow, peakHour, hourlyBuckets }}
 *   peakDow       — 0-6 (Sun–Sat) or null when insufficient data (<5 sightings in 90 days)
 *   peakHour      — 0-23 or null when insufficient data
 *   hourlyBuckets — [{dow, hour, count}] or null; drives the SightingTimelineWidget scatter plot
 */
export function useEntityInsights(entityId) {
  const [trustStats, setTrustStats] = useState(null)
  const [sightingPatternCounts, setSightingPatternCounts] = useState(null)
  const [peakDow, setPeakDow] = useState(null)
  const [peakHour, setPeakHour] = useState(null)
  const [hourlyBuckets, setHourlyBuckets] = useState(null)

  useEffect(() => {
    if (!entityId) return

    let cancelled = false

    entityAPI.getEntityTrustStats(entityId)
      .then(res => { if (!cancelled) setTrustStats(res.data) })
      .catch(err => logger.warn('Trust stats fetch failed', { entityId, err }))

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    entityAPI.getEntitySightingPatterns(entityId, tz)
      .then(res => {
        if (cancelled) return
        const data = res.data ?? {}
        setSightingPatternCounts(data.counts ?? null)
        // peakDow and peakHour are null when the backend deems data insufficient (<5 sightings)
        setPeakDow(data.peakDow ?? null)
        setPeakHour(data.peakHour ?? null)
        // hourlyBuckets drives the 7×24 scatter timeline; null when < minSightingsForPattern
        setHourlyBuckets(data.hourlyBuckets ?? null)
      })
      .catch(err => logger.warn('Sighting patterns fetch failed', { entityId, err }))

    return () => { cancelled = true }
  }, [entityId])

  return { trustStats, sightingPatternCounts, peakDow, peakHour, hourlyBuckets }
}
